// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import type { Observable } from 'rxjs';
import { map } from 'rxjs';

import type { TourStateSource } from '@ergoss/guided-tour';
import { LoggerService } from '@core/services/logger.service';
import { unwrap } from 'src/app/api/unwrap';

/**
 * Backend-backed guided-tour seen-state (`TOUR_STATE_SOURCE` seam) — the piece
 * that makes "present the tour on first connection" roam across devices:
 * PwiApi persists per-crew (opaque subjectId resolved server-side from the
 * crew code; the interceptor's Bearer + context headers are all it needs).
 * Impersonation is the BACKEND's concern by design: super users read an empty
 * map and their writes are skipped side-effect-free, so this impl stays dumb.
 */
@Injectable({ providedIn: 'root' })
export class TourSeenApiService implements TourStateSource {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggerService);

  /** The crew's seen map (tour id → highest seen version). Errors propagate:
   *  the engine is fail-CLOSED on a failed load (auto-starts blocked for the
   *  session; deliberate "?" replays keep working). */
  getSeenVersions(): Observable<Record<string, number>> {
    return this.http.get<{ data?: Record<string, number> | null }>('/api/pwi/tours/seen').pipe(map(unwrap));
  }

  /** Fire-and-forget per the seam contract — PwiApi's upsert is idempotent and
   *  never downgrades, and the engine already raised its in-memory map, so a
   *  lost write costs at most a re-offer on another device. Bare 204, no
   *  envelope. Fail-quiet: a tour must never disturb the page. */
  markSeen(tourId: string, version: number): void {
    this.http.put(`/api/pwi/tours/${encodeURIComponent(tourId)}/seen`, { version }).subscribe({
      error: (error: unknown) => this.logger.error('tours', `Failed to persist seen-state for '${tourId}'`, error),
    });
  }
}
